import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import { useThemeColor } from "@/hooks/useThemeColor";
import auth, { FirebaseAuthTypes } from "@react-native-firebase/auth";
import firestore from "@react-native-firebase/firestore";
import {
  GoogleSignin,
  GoogleSigninButton,
} from "@react-native-google-signin/google-signin";
import { Image } from "expo-image";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Alert,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  useColorScheme,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import PasswordInput from "../../components/ui/PasswordInput";
import { getErrorMessage } from "../../utils/errors";

const LoginScreen = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const theme = useColorScheme();

  const primaryColor = useThemeColor({}, "primary");
  const router = useRouter();

  const resendVerificationEmail = async (
    unverifiedUser: FirebaseAuthTypes.User,
  ) => {
    try {
      await unverifiedUser.sendEmailVerification();
      Alert.alert(t("VERIFICATION_SENT"), t("VERIFICATION_EMAIL_SENT"));
    } catch (error) {
      const code = (error as { code?: string } | null)?.code;
      Alert.alert(
        t("ERROR"),
        code === "auth/too-many-requests"
          ? t("RESEND_TOO_MANY_REQUESTS")
          : getErrorMessage(error),
      );
    } finally {
      await auth().signOut();
    }
  };

  const handleLogin = async () => {
    setAuthLoading(true);
    try {
      await auth().signInWithEmailAndPassword(email, password);

      const user = auth().currentUser;

      await user?.reload();

      if (!user?.emailVerified) {
        if (user) {
          Alert.alert(
            t("EMAIL_NOT_VERIFIED"),
            t("EMAIL_NOT_VERIFIED_RESEND_PROMPT"),
            [
              {
                text: t("CANCEL"),
                style: "cancel",
                onPress: () => {
                  auth().signOut();
                },
              },
              {
                text: t("RESEND"),
                onPress: () => resendVerificationEmail(user),
              },
            ],
            { cancelable: false },
          );
        } else {
          await auth().signOut();
        }
        return;
      }
    } catch (error) {
      Alert.alert(t("LOGIN_ERROR"), getErrorMessage(error));
    } finally {
      setAuthLoading(false);
    }
  };

  const onGoogleButtonPress = async () => {
    setAuthLoading(true);
    try {
      await GoogleSignin.hasPlayServices({
        showPlayServicesUpdateDialog: true,
      });
      await GoogleSignin.signOut();
      const signInResult = await GoogleSignin.signIn();

      const anyResult = signInResult as any;
      const idToken = anyResult?.data?.idToken ?? anyResult?.idToken;
      const googleEmail =
        anyResult?.data?.user?.email ?? anyResult?.user?.email;

      if (!idToken || !googleEmail) {
        throw new Error(t("GOOGLE_SIGN_IN_FAILED_NO_TOKEN"));
      }

      const methods = await auth().fetchSignInMethodsForEmail(googleEmail);
      if (methods.length === 0) {
        Alert.alert(t("NO_ACCOUNT_FOUND"), t("NO_ACCOUNT_FOUND_MESSAGE"));
        return;
      }

      const googleCredential = auth.GoogleAuthProvider.credential(idToken);
      const userCredential = await auth().signInWithCredential(googleCredential);
      const user = userCredential.user;

      // Google can succeed but leave the user at emailVerified: false — this
      // happens when the user previously linked an email/password credential
      // and hasn't clicked the verification link yet. Show the same resend
      // prompt as the email/pw flow instead of silently doing nothing.
      await user.reload();
      if (!user.emailVerified) {
        Alert.alert(
          t("EMAIL_NOT_VERIFIED"),
          t("EMAIL_NOT_VERIFIED_RESEND_PROMPT"),
          [
            {
              text: t("CANCEL"),
              style: "cancel",
              onPress: () => {
                auth().signOut();
              },
            },
            {
              text: t("RESEND"),
              onPress: () => resendVerificationEmail(user),
            },
          ],
          { cancelable: false },
        );
        return;
      }

      try {
        const userDoc = await firestore()
          .collection("users")
          .doc(user.uid)
          .get();

        if (!userDoc.exists) {
          try {
            await user.delete();
          } catch {
            // ignore deletion errors
          }
          await auth().signOut();
          Alert.alert(t("NO_ACCOUNT_FOUND"), t("NO_ACCOUNT_FOUND_MESSAGE"));
        }
      } catch (docErr) {
        // Sign-in already succeeded; Firestore's first read after auth can
        // race the token-propagation window. Let InitialLayout's onSnapshot
        // heal and route once the channel is ready — don't alarm the user.
        console.warn(
          "Post-sign-in orphan check failed; layout will handle redirect:",
          docErr,
        );
      }
    } catch (error) {
      Alert.alert(t("GOOGLE_SIGN_IN_ERROR"), getErrorMessage(error));
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <SafeAreaView
      style={[styles.safeAreaViewContainer, { backgroundColor: "#eee" }]}
      edges={["top", "bottom"]}
    >
      <StatusBar style="dark" animated={true} />

      <ThemedView style={styles.container}>
        {authLoading ? (
          <ActivityIndicator size="large" color="#4285F4" />
        ) : (
          <>
            <ThemedView
              style={{
                alignItems: "center",
              }}
            >
              <Image
                source={require("../../assets/images/vehilink-icon.png")}
                style={{ width: 100, height: 100, borderRadius: 50 }}
                contentFit="cover"
              />
            </ThemedView>
            <ThemedText style={{ marginBottom: 10 }} type="title">
              {t("LOGIN")}
            </ThemedText>
            <TextInput
              style={styles.input}
              placeholder={t("EMAIL")}
              placeholderTextColor="#888"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
            />
            <PasswordInput value={password} onChangeText={setPassword} />
            <TouchableOpacity
              onPress={() => router.push("/(auth)/forgot-password")}
            >
              <ThemedText
                style={[styles.forgotPasswordText, { color: primaryColor }]}
              >
                {t("FORGOT_PASSWORD")}
              </ThemedText>
            </TouchableOpacity>
            <Button
              onPress={handleLogin}
              disabled={!email || !password || authLoading}
              text={t("LOGIN")}
            />
            <ThemedView style={styles.dividerContainer}>
              <ThemedView style={styles.divider} />
              <ThemedText style={styles.orText}>{t("OR")}</ThemedText>
              <ThemedView style={styles.divider} />
            </ThemedView>
            <GoogleSigninButton
              size={GoogleSigninButton.Size.Wide}
              style={styles.googleButton}
              color={
                GoogleSigninButton.Color[theme === "dark" ? "Dark" : "Light"]
              }
              onPress={onGoogleButtonPress}
              disabled={authLoading}
            />
            <TouchableOpacity
              style={styles.signupButton}
              onPress={() => router.replace("/(auth)/signup" as any)}
            >
              <ThemedText
                style={[styles.signupText, { color: primaryColor }]}
              >
                {t("DONT_HAVE_ACCOUNT")} {t("SIGN_UP")}
              </ThemedText>
            </TouchableOpacity>
          </>
        )}
      </ThemedView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeAreaViewContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
    backgroundColor: "#fff",
  },
  input: {
    width: "100%",
    maxWidth: 350,
    height: 48,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    marginBottom: 16,
    fontSize: 16,
    backgroundColor: "#fafafa",
    color: "#000",
  },
  dividerContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 16,
    marginBottom: 0,
  },
  divider: {
    flex: 1,
    height: 1,
    backgroundColor: "#ccc",
    marginHorizontal: 8,
  },
  orText: {
    fontSize: 16,
  },
  googleButton: {
    marginTop: 5,
    paddingVertical: 12,
    paddingHorizontal: 30,
    borderRadius: 8,
    width: "100%",
    maxWidth: 350,
    alignItems: "center",
  },
  signupButton: {
    marginTop: 24,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  signupText: {
    fontSize: 16,
    textAlign: "center",
  },
  forgotPasswordText: {
    fontSize: 16,
    textAlign: "center",
    marginBottom: 16,
  },
});

export default LoginScreen;
